import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Shield,
  FileText,
  HeartHandshake,
  Calendar,
  Mail,
  Printer,
  Search,
  ArrowUp,
  Check,
  Copy,
  ChevronRight,
  BadgeCheck,
  Info,
} from "lucide-react";
import Bannerimage from "../../Assets/Images/Bannerimage.png";

const POLICIES = [
  {
    name: "Privacy Policy",
    path: "/privacy-policy",
    icon: Shield,
    description: "How we collect, protect, and handle your data",
  },
  {
    name: "Terms & Conditions",
    path: "/terms-conditions",
    icon: FileText,
    description: "Guidelines and legal terms governing platform usage",
  },
  {
    name: "Child Safety Policy",
    path: "/child-safety-policy",
    icon: HeartHandshake,
    description: "Our zero-tolerance safeguarding standards for minors",
  },
];

const PolicyPageTemplate = ({
  title,
  subtitle,
  dateLabel = "LAST UPDATED",
  dateValue = "1 January 2025",
  contactEmail = "legal@aafsa.org",
  contactRole = "Compliance Officer",
  intro,
  sections = [],
  policyIcon: PolicyIcon = Shield,
}) => {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSection, setActiveSection] = useState("1");
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Set document title for SEO
  useEffect(() => {
    document.title = `${title} | AAFSA - Aryalex Foundation`;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [title]);

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      for (const section of sections) {
        const el = document.getElementById(`section-${section.number}`);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section.number);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  const scrollToSection = (sectionNumber) => {
    const el = document.getElementById(`section-${sectionNumber}`);
    if (el) {
      const yOffset = -100;
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
      setActiveSection(sectionNumber);
    }
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(contactEmail);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handlePrint = () => {
    window.print();
  };

  // Filter sections by search query
  const filteredSections = sections.filter((s) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      s.title.toLowerCase().includes(q) ||
      s.body.toLowerCase().includes(q) ||
      s.number.includes(q)
    );
  });

  return (
    <div className="bg-[#f8fafc] text-gray-800 min-h-screen">
      {/* ================= HERO HEADER BANNER ================= */}
      <div className="relative w-full bg-[#02323F] overflow-hidden text-white">
        {/* Background Image with overlay */}
        <div className="absolute inset-0">
          <img
            src={Bannerimage}
            alt="Policy Banner Background"
            className="w-full h-full object-cover object-center opacity-20 transform scale-105 transition-transform duration-1000"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#02323F] via-[#02323F]/95 to-[#01252f]/90" />
        </div>

        {/* Hero Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 sm:pt-12 sm:pb-16">
          {/* Breadcrumbs */}
          <nav
            aria-label="Breadcrumb"
            className="flex items-center space-x-2 text-xs sm:text-sm text-gray-300 mb-6"
          >
            <Link
              to="/"
              className="hover:text-white transition duration-200 underline-offset-4 hover:underline"
            >
              Home
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className="text-gray-300">Policies</span>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className="text-white font-medium">{title}</span>
          </nav>

          {/* Title & Badges */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/20 border border-teal-400/30 text-teal-300 text-xs sm:text-sm font-medium mb-4">
                <BadgeCheck className="w-4 h-4 text-teal-400" />
                <span>Official Legal Policy Document</span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold font-onest text-white tracking-tight leading-tight">
                {title}
              </h1>

              {subtitle && (
                <p className="mt-3 text-sm sm:text-base text-gray-300 leading-relaxed max-w-2xl">
                  {subtitle}
                </p>
              )}

              {/* Meta Info */}
              <div className="mt-5 flex flex-wrap items-center gap-4 text-xs sm:text-sm text-gray-300">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/10">
                  <Calendar className="w-4 h-4 text-teal-300" />
                  <span className="uppercase tracking-wider font-semibold text-gray-300">
                    {dateLabel}:
                  </span>
                  <span className="text-white font-bold">{dateValue}</span>
                </div>

                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/10">
                  <PolicyIcon className="w-4 h-4 text-teal-300" />
                  <span>Aryalex Foundation (AAFSA)</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap lg:flex-col sm:flex-row gap-3 pt-2 lg:pt-0">
              <button
                type="button"
                onClick={handlePrint}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs sm:text-sm font-medium transition duration-200 backdrop-blur-sm shadow-sm"
                title="Print or Save as PDF"
              >
                <Printer className="w-4 h-4 text-teal-300" />
                <span>Print / Save PDF</span>
              </button>

              <button
                type="button"
                onClick={handleCopyLink}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs sm:text-sm font-medium transition duration-200 backdrop-blur-sm shadow-sm"
                title="Copy shareable link"
              >
                {copiedLink ? (
                  <>
                    <Check className="w-4 h-4 text-emerald-400" />
                    <span className="text-emerald-300">Link Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 text-teal-300" />
                    <span>Share Policy</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ================= POLICY SWITCHER TABS ================= */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-20 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 sm:gap-4 overflow-x-auto py-3 noScrollbar">
            {POLICIES.map((policy) => {
              const Icon = policy.icon;
              const isActive = location.pathname === policy.path;
              return (
                <Link
                  key={policy.path}
                  to={policy.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition-all duration-200 ${
                    isActive
                      ? "bg-[#02323F] text-white shadow-sm"
                      : "text-gray-600 hover:text-[#02323F] hover:bg-gray-100"
                  }`}
                >
                  <Icon
                    className={`w-4 h-4 ${
                      isActive ? "text-teal-300" : "text-gray-400"
                    }`}
                  />
                  <span>{policy.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* ================= MAIN CONTAINER ================= */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* ================= LEFT SIDEBAR (Sticky) ================= */}
          <aside className="lg:col-span-4 space-y-6">
            {/* Quick Table of Contents Card */}
            <div className="bg-white rounded-2xl border border-gray-200/80 p-5 shadow-xs sticky top-20">
              <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                <h3 className="font-bold text-gray-900 text-sm sm:text-base flex items-center gap-2 font-onest">
                  <span className="w-2 h-2 rounded-full bg-teal-500" />
                  Table of Contents
                </h3>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-medium">
                  {sections.length} Sections
                </span>
              </div>

              {/* Navigation list */}
              <ul className="mt-4 space-y-1 max-h-[50vh] overflow-y-auto pr-1 thin-scrollbar">
                {sections.map((section) => {
                  const isCurrent = activeSection === section.number;
                  return (
                    <li key={section.number}>
                      <button
                        type="button"
                        onClick={() => scrollToSection(section.number)}
                        className={`w-full text-left px-3 py-2 rounded-xl text-xs sm:text-sm transition-all duration-150 flex items-center gap-2.5 ${
                          isCurrent
                            ? "bg-teal-50 text-[#02323F] font-semibold border-l-4 border-teal-500"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        }`}
                      >
                        <span
                          className={`w-5 h-5 rounded-md text-[10px] flex items-center justify-center font-bold shrink-0 ${
                            isCurrent
                              ? "bg-[#02323F] text-white"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {section.number}
                        </span>
                        <span className="truncate">{section.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              {/* Contact Card in Sidebar */}
              <div className="mt-6 pt-5 border-t border-gray-100 bg-gradient-to-br from-teal-50/60 to-blue-50/40 rounded-xl p-4 border border-teal-100/50">
                <div className="flex items-center gap-2 text-[#02323F] font-semibold text-xs sm:text-sm mb-1.5">
                  <Mail className="w-4 h-4 text-teal-600" />
                  <span>Have questions about this?</span>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed mb-3">
                  Reach out directly to our {contactRole}. We are committed to
                  prompt, transparent resolution.
                </p>

                <div className="flex items-center justify-between bg-white rounded-lg p-2 border border-teal-200/60 shadow-xs">
                  <a
                    href={`mailto:${contactEmail}`}
                    className="text-xs text-teal-700 font-semibold hover:underline truncate"
                  >
                    {contactEmail}
                  </a>
                  <button
                    type="button"
                    onClick={handleCopyEmail}
                    className="p-1 text-gray-500 hover:text-teal-600 transition"
                    title="Copy Email"
                  >
                    {copiedEmail ? (
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </aside>

          {/* ================= RIGHT COLUMN (Main Content) ================= */}
          <article className="lg:col-span-8 space-y-6">
            {/* Executive Intro Callout Box */}
            {intro && (
              <div className="bg-gradient-to-r from-teal-900 to-[#02323F] text-white rounded-2xl p-6 sm:p-7 shadow-sm border border-teal-800 relative overflow-hidden">
                <div className="absolute right-0 top-0 transform translate-x-8 -translate-y-8 w-40 h-40 bg-teal-400/10 rounded-full blur-2xl pointer-events-none" />
                <div className="flex items-start gap-3.5 relative z-10">
                  <div className="p-2.5 rounded-xl bg-white/10 text-teal-300 shrink-0 mt-0.5">
                    <Info className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-xs uppercase tracking-wider text-teal-300 font-bold mb-1.5">
                      Executive Summary & Commitment
                    </h2>
                    <p className="text-sm sm:text-base text-gray-100 leading-relaxed font-sans font-normal">
                      {intro}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Keyword Search Bar */}
            <div className="bg-white rounded-2xl border border-gray-200/80 p-3 sm:p-4 shadow-xs">
              <div className="relative flex items-center">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search in this policy (e.g. data, voluntary, reporting, cookies)..."
                  className="w-full pl-9 pr-10 py-2 text-xs sm:text-sm bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-all placeholder:text-gray-400"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 text-xs text-gray-400 hover:text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-full w-5 h-5 flex items-center justify-center font-bold"
                  >
                    ×
                  </button>
                )}
              </div>
              {searchQuery && (
                <div className="mt-2 text-xs text-gray-500 px-1 flex items-center justify-between">
                  <span>
                    Found{" "}
                    <strong className="text-gray-900">
                      {filteredSections.length}
                    </strong>{" "}
                    section
                    {filteredSections.length === 1 ? "" : "s"} matching &quot;
                    {searchQuery}&quot;
                  </span>
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="text-teal-600 hover:underline font-medium"
                  >
                    Clear filter
                  </button>
                </div>
              )}
            </div>

            {/* Section Cards */}
            <div className="space-y-4 sm:space-y-6">
              {filteredSections.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                  <Search className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                    No matching sections found
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">
                    Try searching with different terms or reset your filter.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="mt-4 px-4 py-2 bg-[#02323F] text-white rounded-xl text-xs font-semibold hover:bg-teal-900 transition"
                  >
                    Reset Search
                  </button>
                </div>
              ) : (
                filteredSections.map((section) => {
                  const isCurrent = activeSection === section.number;
                  return (
                    <section
                      id={`section-${section.number}`}
                      key={section.number}
                      className={`bg-white rounded-2xl border transition-all duration-200 p-5 sm:p-7 shadow-xs scroll-mt-28 ${
                        isCurrent
                          ? "border-teal-400 ring-2 ring-teal-500/10 shadow-sm"
                          : "border-gray-200/80 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-start gap-3.5 sm:gap-4">
                        {/* Number Badge */}
                        <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-gradient-to-tr from-[#02323F] to-teal-600 text-white flex items-center justify-center font-bold text-xs sm:text-sm shrink-0 shadow-xs mt-0.5">
                          {section.number}
                        </div>

                        {/* Title & Body */}
                        <div className="flex-1 min-w-0">
                          <h2 className="text-base sm:text-lg md:text-xl font-bold font-onest text-gray-900 tracking-tight mb-2.5">
                            {section.title}
                          </h2>
                          <div className="text-xs sm:text-sm md:text-base text-gray-600 leading-relaxed font-sans font-normal">
                            {section.body}
                          </div>
                        </div>
                      </div>
                    </section>
                  );
                })
              )}
            </div>

            {/* Bottom Official Seal & Assistance Card */}
            <div className="bg-white rounded-2xl border border-gray-200/80 p-6 sm:p-8 shadow-xs">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-6 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center font-bold">
                    <PolicyIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm sm:text-base font-onest">
                      Aryalex Foundation (AAFSA)
                    </h4>
                    <p className="text-xs text-gray-500">
                      Non-profit & social development organisation • Reg. 2022
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-xs text-teal-700 hover:text-teal-900 font-semibold bg-teal-50 hover:bg-teal-100 rounded-lg transition"
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                  <span>Back to top</span>
                </button>
              </div>

              <div className="mt-5 text-xs text-gray-500 leading-relaxed space-y-2">
                <p>
                  This official document is governed under the jurisdiction and
                  applicable non-profit safeguarding standards of India. If you
                  have questions or wish to exercise your legal rights regarding
                  this policy, contact us directly at{" "}
                  <a
                    href={`mailto:${contactEmail}`}
                    className="text-teal-700 font-semibold underline hover:text-teal-900"
                  >
                    {contactEmail}
                  </a>
                  .
                </p>
                <p className="text-gray-400">
                  © {new Date().getFullYear()} Aryalex Foundation (AAFSA). All
                  rights reserved.
                </p>
              </div>
            </div>
          </article>
        </div>
      </main>
    </div>
  );
};

export default PolicyPageTemplate;
